import React from "react";
import { Link } from "react-router-dom";
import { Marquee } from "@/components/Marquee";
import { useSeo } from "@/hooks/useSeo";

const HERO = "/products/HYEAMAN-mens-streetwear-collection.png";

const LOOKS = [
  {
    n: "01", title: "The Arrival", img: "/products/hyeaman-01-midnight-tailoring.png",
    blurb: "A commanding outer layer worn over a clean monochromatic foundation. The oversized silhouette establishes presence without excess. Minimal styling allows the construction, texture, and HYEAMAN identity to lead.",
    mood: "Composed. Fearless. Intentional.",
    palette: [["Midnight", "#080808"], ["Graphite", "#45474b"], ["Silver", "#c9c9cf"]],
    pieces: "Statement jacket · Heavyweight T-shirt · Relaxed trousers",
  },
  {
    n: "02", title: "Northern Ground", img: "/products/hyeaman-03-emerald-night-utility.png",
    blurb: "Practical Canadian influences refined through a luxury streetwear lens. Layered fabrics and generous proportions provide warmth and movement, while precise details keep the look polished — designed for changing weather, changing cities, and lives that never stay in one place.",
    mood: "Resilient. Grounded. Elevated.",
    palette: [["Stone", "#c8c1b2"], ["Forest", "#2f3d2f"], ["Charcoal", "#3a3a3c"]],
    pieces: "Technical outerwear · Premium hoodie · Utility trousers",
  },
  {
    n: "03", title: "Shared Language", img: "/products/hyeaman-02-ivory-architecture.png",
    blurb: "A balance of cultural references expressed through texture, proportion, and detail. Rather than belonging to one tradition, the look creates a new visual language from many influences — familiar elements reimagined with respect and brought together as one cohesive statement.",
    mood: "Connected. Open. Expressive.",
    palette: [["Earth", "#6b4f3a"], ["Cream", "#f0e6d2"], ["Muted Gold", "#cda95c"]],
    pieces: "Textured overshirt · Layered knit · Wide-leg trousers",
  },
  {
    n: "04", title: "City Rhythm", img: "/products/hyeaman-04-cobalt-motion.png",
    blurb: "An everyday uniform for people who move with purpose. Relaxed tailoring meets the energy of the street. The silhouette is effortless, but every element feels deliberate — from the weight of the fabric to the placement of each finish.",
    mood: "Fluid. Independent. Alive.",
    palette: [["Concrete", "#9a9a97"], ["White", "#f5f4ef"], ["Electric Blue", "#2f6fe0"]],
    pieces: "Tailored bomber · Fitted base layer · Pleated pants",
  },
  {
    n: "05", title: "Beyond Borders", img: "/products/hyeaman-05-burgundy-deconstruction.png",
    blurb: "A statement look that celebrates identity without reducing it to a label. Bold proportions and signature branding create a sense of unity, while adaptable styling leaves room for the wearer’s personality. The piece becomes part of their story — not the other way around.",
    mood: "Unrestricted. Proud. Unifying.",
    palette: [["Black", "#080808"], ["Crimson", "#8a1f2d"], ["Bone", "#e7e0cf"]],
    pieces: "Graphic outerwear · Signature top · Sculpted denim",
  },
  {
    n: "06", title: "After Dark", img: "/products/HYEAMAN-mens-streetwear-collection.png",
    blurb: "Luxury expressed through restraint. Deep tones, subtle shine, and sharp layering transform streetwear for the evening. The confidence comes from the silhouette and materials rather than unnecessary decoration.",
    mood: "Magnetic. Refined. Unapologetic.",
    palette: [["Obsidian", "#16181c"], ["Burgundy", "#5e2230"], ["Brushed Metal", "#b8b8bd"]],
    pieces: "Cropped jacket · Tonal shirt · Streamlined trousers",
  },
  {
    n: "07", title: "One Future", img: "/products/HYEAMAN-ladies-streetwear-collection.png",
    blurb: "The closing look represents HYEAMAN’s vision: different identities moving forward together. Soft and structured elements coexist. Masculine and feminine codes overlap. Cultural boundaries dissolve into a silhouette that feels individual, inclusive, and unmistakably modern.",
    mood: "Hopeful. United. Limitless.",
    palette: [["Cloud White", "#f5f6f7"], ["Sand", "#d9c4a3"], ["Silver", "#cfd2d6"]],
    pieces: "Sculptural coat · Layered essentials · Flowing trousers",
  },
];

export default function Lookbook() {
  useSeo({
    title: "Lookbook — HYEAMAN Campaigns | Meet the World",
    description: "Explore the HYEAMAN lookbook: global luxury streetwear campaigns embodying Meet the World and the Street Sheriff — many cultures, one movement, nobody left behind.",
    path: "/lookbook",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "HYEAMAN Lookbook — Meet the World",
      url: "https://hyeamanbrand.com/lookbook",
      description: "Global luxury streetwear campaigns embodying Meet the World and the Street Sheriff — many cultures, one movement, nobody left behind.",
      isPartOf: { "@type": "WebSite", name: "HYEAMAN", url: "https://hyeamanbrand.com" },
    },
  });
  return (
    <div data-testid="lookbook-page">
      {/* HERO */}
      <section className="relative flex min-h-[88vh] items-end overflow-hidden">
        <img src={HERO} alt="HYEAMAN Lookbook" className="absolute inset-0 h-full w-full object-cover hero-zoom" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 sm:px-8 lg:px-12">
          <p className="overline-label reveal mb-4">HYEAMAN · Street Sheriff — Lookbook Volume I</p>
          <h1 className="reveal font-black uppercase leading-[0.88] tracking-tight text-cream text-5xl sm:text-7xl lg:text-8xl" style={{ animationDelay: "80ms" }}>
            No One<br /><span className="font-serif font-normal italic text-gradient-gold">Left Behind</span>
          </h1>
          <p className="reveal mt-6 max-w-xl text-cream/70" style={{ animationDelay: "160ms" }}>
            HYEAMAN, also known as Street Sheriff — Canadian premium luxury streetwear for a world where many cultures and lifestyles move as one movement.
          </p>
          <p className="reveal mt-4 font-mono text-[0.7rem] uppercase tracking-[0.35em] text-gold/70" style={{ animationDelay: "200ms" }} data-testid="lookbook-slogans">
            Wear Confidence · No One Is Left Behind
          </p>
        </div>
      </section>

      <Marquee />

      {/* MEET THE WORLD */}
      <Prose kicker="Meet the World" title="Different backgrounds. Same streets." testid="lookbook-meet-the-world">
        <p>Hyeaman is a global canvas where cultures, nationalities, backgrounds, and personalities meet. Street Sheriff brings the urban armor — protecting unity and claiming the streets for everyone.</p>
        <p className="font-serif text-xl italic text-gold">Different backgrounds. Same streets. Welcome to the Nations.</p>
      </Prose>

      {/* THE WORLD */}
      <Prose kicker="The World of HYEAMAN" title="Made for everyone creating a place of their own">
        <p>HYEAMAN is Canadian premium luxury streetwear shaped by the meeting of cultures, identities, and ways of life. Rooted in Canada’s multicultural spirit, the brand brings different worlds together through confident silhouettes, elevated materials, and thoughtful details.</p>
        <p>Every piece is designed to move beyond borders — between street and luxury, heritage and modernity, individuality and community. <span className="text-gold">Nobody is left behind.</span></p>
      </Prose>

      {/* THE COLLECTION */}
      <Prose kicker="The Collection" title="Connection through contrast">
        <p>Structured outerwear meets relaxed proportions. Refined finishes appear beside raw textures. Quiet essentials balance bold statement pieces. Each look reflects the freedom to belong to more than one culture, community, or creative world.</p>
        <p>The result is streetwear with presence: sophisticated enough to be timeless, comfortable enough for everyday life, and distinctive enough to speak before the wearer says a word.</p>
      </Prose>

      {/* LOOKS */}
      <div className="border-t border-white/10">
        {LOOKS.map((look, i) => (
          <section key={look.n} className="border-b border-white/10" data-testid={`look-${look.n}`}>
            <div className={`mx-auto grid max-w-7xl items-stretch gap-0 lg:grid-cols-2`}>
              <div className={`relative aspect-[4/5] overflow-hidden bg-ink lg:aspect-auto lg:min-h-[600px] ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img src={look.img} alt={`Look ${look.n} — ${look.title}`} className="h-full w-full object-cover" loading="lazy" />
                <span className="absolute left-6 top-6 font-serif text-6xl italic text-gold/80 drop-shadow-lg">{look.n}</span>
              </div>
              <div className="flex flex-col justify-center px-4 py-12 sm:px-10 lg:px-16">
                <p className="overline-label mb-2">Look {look.n}</p>
                <h3 className="font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">{look.title}</h3>
                <p className="mt-5 max-w-md leading-relaxed text-cream/70">{look.blurb}</p>
                <p className="mt-6 font-serif text-lg italic text-gold">{look.mood}</p>

                <div className="mt-6">
                  <p className="overline-label mb-2 text-[0.6rem]">Palette</p>
                  <div className="flex flex-wrap gap-3">
                    {look.palette.map(([name, hex]) => (
                      <span key={name} className="inline-flex items-center gap-2 text-xs text-cream/60">
                        <span className="h-5 w-5 rounded-full border border-white/20" style={{ backgroundColor: hex }} /> {name}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <p className="overline-label mb-1 text-[0.6rem]">Key Pieces</p>
                  <p className="text-sm text-cream/70">{look.pieces}</p>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* CRAFTED WITH PURPOSE */}
      <Prose kicker="Crafted With Purpose" title="Luxury should be felt — not only seen">
        <p>Premium fabrics, considered construction, and enduring silhouettes give every garment substance. Comfort is treated as part of craftsmanship. Inclusivity is built into the design through adaptable shapes, thoughtful sizing, and pieces that invite personal interpretation.</p>
        <p className="font-serif text-xl italic text-cream">These are clothes meant to live beyond a season. To be worn differently. To gather memories. To become personal.</p>
      </Prose>

      {/* COMMUNITY */}
      <section className="border-y border-gold/20 bg-ink" data-testid="community-section">
        <div className="mx-auto max-w-4xl px-4 py-24 text-center sm:px-8">
          <p className="overline-label mb-6">The HYEAMAN Community</p>
          <p className="font-serif text-2xl italic leading-relaxed text-cream sm:text-3xl">
            "Our differences are not barriers. They are the material from which something greater can be created."
          </p>
          <p className="mt-8 mx-auto max-w-2xl text-cream/70">
            HYEAMAN belongs to the person balancing tradition with ambition. The artist creating between cultures. The newcomer building a home. The individual who refuses to fit inside a single category.
          </p>
          <div className="mt-10 flex flex-col items-center gap-1 font-serif text-xl text-gold">
            <span>Everyone brings something.</span>
            <span>Everyone belongs.</span>
            <span className="text-gold-bright">Nobody is left behind.</span>
          </div>
        </div>
      </section>

      {/* CREATIVE DIRECTION */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-12">
        <p className="overline-label mb-2">Creative Direction</p>
        <h2 className="mb-10 font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">Behind the Vision</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[
            ["Photography", "Cinematic editorial imagery combining modern Canadian architecture, natural landscapes, and multicultural city environments."],
            ["Casting", "A diverse, intergenerational cast representing different cultures, identities, body types, and creative communities."],
            ["Styling", "Layered, gender-fluid, and personal. Pieces feel intentionally composed without appearing overly polished."],
            ["Lighting", "Cool natural daylight for outdoor imagery, contrasted with warm directional light for portraits and evening looks."],
            ["Visual Tone", "Premium, inclusive, bold, and emotionally grounded."],
            ["The Movement", "Many cultures. Many lifestyles. One movement — for a world where nobody is left behind."],
          ].map(([t, d]) => (
            <div key={t} className="border border-white/10 bg-ink p-6">
              <h3 className="font-serif text-lg text-gold">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-cream/60">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="border-t border-white/10 bg-ink">
        <div className="mx-auto flex max-w-4xl flex-col items-center px-4 py-20 text-center sm:px-8">
          <h2 className="font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl">Wear the Movement</h2>
          <p className="mt-4 max-w-md text-cream/60">Many cultures. Many lifestyles. One movement. Explore the collection and make a place of your own.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/shop" data-testid="lookbook-shop-cta" className="bg-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">Shop the Collection</Link>
            <Link to="/street-sheriff" data-testid="lookbook-ss-cta" className="border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-widest text-cream transition hover:bg-gold/10">Street Sheriff</Link>
            <Link to="/about" className="border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-widest text-cream transition hover:bg-gold/10">Our Story</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Prose({ kicker, title, children, testid }) {
  return (
    <section className="mx-auto max-w-3xl px-4 py-16 sm:px-8" data-testid={testid}>
      <p className="overline-label mb-3">{kicker}</p>
      <h2 className="mb-6 font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">{title}</h2>
      <div className="space-y-5 leading-relaxed text-cream/70">{children}</div>
    </section>
  );
}
